import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { motion } from "framer-motion";
import { HiOutlineSparkles } from "react-icons/hi";
import { useEffect, useState } from 'react';
import axios from '../utils/axios';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from 'react-router-dom';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { TaskListItem } from '@/components/TaskListItem';
import { ProjectListItem } from '@/components/ProjectListItem';
import { MonitorListItem } from '@/components/MonitorListItem';
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area"

const fadeInVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 }
};

import { SEO } from '@/components/SEO';
import StatusBotIcon from '@/components/StatusBotIcon';

// Ai Command Palette
import { useAiPageContext } from '@/context/AiPageContextProvider';
import { contextHelpers } from "@/components/AiCommandPalette";

// Welcome Row Component
const WelcomeRow = ({ loading, error, data }) => {
    if (loading) {
        return <WelcomeLoadingSkeleton />;
    }

    if (error) {
        return (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-12 mt-4">
                <div className="text-destructive">Failed to load welcome message</div>
            </div>
        );
    }

    if (!data) return null;

    return (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-12 mt-4">
            <h1 className="text-3xl sm:text-4xl md:text-5xl tracking-tight">
                {data.welcomeMessage}
            </h1>
            
            <div className="text-sm text-muted-foreground shrink-0">
                <TooltipProvider delayDuration={0}>
                    <Tooltip>
                        <TooltipTrigger className="text-sm text-muted-foreground hover:text-primary cursor-pointer transition-colors">
                            {(() => {
                                const { weekNumber, daysRemaining, hoursRemaining } = data.weekData;
                                if (daysRemaining < 1) {
                                    return `Week ${weekNumber} • ${hoursRemaining} hour${hoursRemaining !== 1 ? 's' : ''} left`;
                                }
                                return `Week ${weekNumber} • ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} left`;
                            })()}
                        </TooltipTrigger>
                        <TooltipContent>
                            {`${data.weekData.startDate} – ${data.weekData.endDate}`}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
        </div>
    );
};

// Bot Insight Component
const BotInsight = ({ loading, error, data }) => {
    if (loading) {
        return <InsightLoadingSkeleton />;
    }

    if (error) {
        return (
            <Card className="mb-12 relative overflow-hidden border-0 bg-gradient-to-br from-violet-600 to-indigo-600 shadow-xl">
                <CardContent className="relative px-4 py-6 sm:px-6 md:px-10 md:py-10">
                    <div className="flex-1 space-y-4">
                        <div className="flex flex-row items-center gap-3">
                            <h2 className="text-xl sm:text-2xl font-semibold text-white/95">
                                Bot Insight
                            </h2>
                            <span className="w-fit text-xs bg-white/20 dark:bg-white/10 text-white px-2 py-1 rounded-full flex items-center">
                                <StatusBotIcon className="w-4 h-4 mr-1" />
                                AI-Generated
                            </span>
                        </div>
                        <p className="text-base sm:text-lg leading-relaxed text-white/85 font-light">
                            Unable to load insight message, check back later.
                        </p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!data?.insightMessage) return null;

    return (
        <Card className="mb-12 relative overflow-hidden border-0 bg-gradient-to-br from-violet-600 to-indigo-600 shadow-xl">
            <CardContent className="relative px-4 py-6 sm:px-6 md:px-10 md:py-10">
                <div className="flex-1 space-y-4">
                    <div className="flex flex-row items-center gap-3">
                        <h2 className="text-xl sm:text-2xl font-semibold text-white/95">
                            Bot Insight
                        </h2>
                        <span className="w-fit text-xs bg-white/20 dark:bg-white/10 text-white px-2 py-1 rounded-full flex items-center">
                            <StatusBotIcon className="w-4 h-4 mr-1" />
                            AI-Generated
                        </span>
                    </div>
                    <p className="text-base sm:text-lg leading-relaxed text-white/85 font-light">
                        {data.insightMessage}
                    </p>
                </div>
            </CardContent>
        </Card>
    );
};

// Dashboard Cards Component
const DashboardCards = ({ loading, error, data, navigate }) => {
    if (loading) {
        return <CardLoadingSkeleton />;
    }

    if (error) {
        return (
            <Card>
                <CardContent className="px-6 py-8">
                    <div className="text-destructive">Failed to load dashboard data</div>
                </CardContent>
            </Card>
        );
    }

    if (!data) return null;

    return (
        <div className="space-y-6 sm:space-y-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
                {/* Assigned Tasks Card */}
                <Card className="col-span-1">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                        <CardTitle className="text-sm font-medium">Assigned Tasks</CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/plan')}>
                            View All
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <ScrollArea className="w-full">
                            {data.assignedTasks?.length > 0 ? (
                                <div className="space-y-4">
                                    {data.assignedTasks.map(task => (
                                        <TaskListItem
                                            key={task.id}
                                            task={task}
                                            organizationSlug={task.organization.slug}
                                            columnConfig={{
                                                checkbox: false,
                                                key: true,
                                                summary: true,
                                                assignee: false,
                                                priority: true,
                                                dueDate: false,
                                                actions: false
                                            }}
                                            className="min-w-[400px]"
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-12 text-center">
                                    <p className="text-sm text-muted-foreground mb-4">
                                        No tasks currently assigned to you
                                    </p>
                                    <Button variant="outline" size="sm" onClick={() => navigate('/plan')}>
                                        Browse projects
                                    </Button>
                                </div>
                            )}
                            <ScrollBar orientation="horizontal" />
                        </ScrollArea>
                    </CardContent>
                </Card>

                {/* Projects Card */}
                <Card className="col-span-1">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                        <CardTitle className="text-sm font-medium">Recent Projects</CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/plan')}>
                            View All
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <ScrollArea className="w-full">
                            {data?.projects?.length > 0 ? (
                                <div className="space-y-4">
                                    {data.projects.map(project => (
                                        <ProjectListItem
                                            key={project.id}
                                            project={project}
                                            columnConfig={{
                                                name: true,
                                                key: true,
                                                organization: true,
                                                taskCount: true,
                                                role: false,
                                                actions: false
                                            }}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-12 text-center">
                                    <p className="text-sm text-muted-foreground mb-4">
                                        No recent projects in your organizations
                                    </p>
                                    <Button variant="outline" size="sm" onClick={() => navigate('/plan/new')}>
                                        Create a new project
                                    </Button>
                                </div>
                            )}
                            <ScrollBar orientation="horizontal" />
                        </ScrollArea>
                    </CardContent>
                </Card>
            </div>

            {/* Monitors Card */}
            <div className="grid grid-cols-1">
                <Card className="col-span-1">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                        <CardTitle className="text-sm font-medium">Active Monitors</CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/monitors')}>
                            View All
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <ScrollArea className="w-full whitespace-nowrap">
                            {data?.monitors?.length > 0 ? (
                                <div className="space-y-4">
                                    {data.monitors.map(monitor => (
                                        <MonitorListItem
                                            key={monitor.id}
                                            monitor={monitor}
                                            columnConfig={{
                                                status: true,
                                                name: true,
                                                responseTime: true,
                                                lastRun: true,
                                                actions: true
                                            }}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-12 text-center">
                                    <p className="text-sm text-muted-foreground mb-4">
                                        Keep track of your services in real-time
                                    </p>
                                    <Button variant="outline" size="sm" onClick={() => navigate('/monitors/new')}>
                                        Set up monitoring
                                    </Button>
                                </div>
                            )}
                            <ScrollBar orientation="horizontal" />
                        </ScrollArea>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

// Welcome Message Loading Skeleton
const WelcomeLoadingSkeleton = () => (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-12 mt-4">
        <Skeleton className="h-10 sm:h-12 w-[270px] sm:w-[400px]" />
        <Skeleton className="h-5 sm:h-6 w-[90px] sm:w-[150px]" />
    </div>
);

// Bot Insight Loading Skeleton
const InsightLoadingSkeleton = () => (
    <Card className="mb-12 relative overflow-hidden border-0 bg-gradient-to-br from-violet-600 to-indigo-600 shadow-xl">
        <CardContent className="relative px-4 py-6 sm:px-6 md:px-10 md:py-10">
            <div className="flex-1 space-y-4">
                <div className="flex flex-row sm:items-center gap-3">
                    <h2 className="text-xl sm:text-2xl font-semibold text-white/95">
                        Bot Insight
                    </h2>
                    <span className="w-fit text-xs bg-white/20 dark:bg-white/10 text-white px-2 py-1 rounded-full flex items-center">
                        <StatusBotIcon className="w-4 h-4 mr-1" />
                        AI-Generated
                    </span>
                </div>
                <div className="space-y-2">
                    <Skeleton className="h-6 w-[90%] bg-white/20" />
                    <Skeleton className="h-6 w-[75%] bg-white/20" />
                </div>
            </div>
        </CardContent>
    </Card>
);

// Card Section Loading Skeleton
const CardLoadingSkeleton = () => (
    <div className="space-y-6 sm:space-y-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
            {/* Tasks Card Skeleton */}
            <Card className="col-span-1">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                    <CardTitle className="text-sm font-medium">Assigned Tasks</CardTitle>
                    <Button variant="ghost" size="sm" disabled>View All</Button>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <Skeleton key={i} className="h-16 w-full" />
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Projects Card Skeleton */}
            <Card className="col-span-1">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                    <CardTitle className="text-sm font-medium">Recent Projects</CardTitle>
                    <Button variant="ghost" size="sm" disabled>View All</Button>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <Skeleton key={i} className="h-16 w-full" />
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>

        {/* Monitors Card Skeleton */}
        <Card className="w-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle className="text-sm font-medium">Active Monitors</CardTitle>
                <Button variant="ghost" size="sm" disabled>View All</Button>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                        <Skeleton key={i} className="h-16 w-full" />
                    ))}
                </div>
            </CardContent>
        </Card>
    </div>
);

const TodayPage = () => {
    const { user, axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { setCurrentAiPageContext } = useAiPageContext();
    
    // Separate state for each data type
    const [basicLoading, setBasicLoading] = useState(true);
    const [insightLoading, setInsightLoading] = useState(true);
    const [cardsLoading, setCardsLoading] = useState(true);
    const [basicData, setBasicData] = useState(null);
    const [insightData, setInsightData] = useState(null);
    const [cardsData, setCardsData] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const basicPromise = axiosInstance.get('/today/basic')
                    .then(response => {
                        setBasicData(response.data);
                        setBasicLoading(false);
                    })
                    .catch(error => {
                        console.error('Error fetching basic data:', error);
                        setBasicLoading(false);
                    });

                const insightPromise = axiosInstance.get('/today/insight')
                    .then(response => {
                        setInsightData(response.data);
                        setInsightLoading(false);
                    })
                    .catch(error => {
                        console.error('Error fetching insight data:', error);
                        setInsightLoading(false);
                    });

                const cardsPromise = axiosInstance.get('/today/cards')
                    .then(response => {
                        setCardsData(response.data);
                        setCardsLoading(false);
                    })
                    .catch(error => {
                        console.error('Error fetching cards data:', error);
                        setCardsLoading(false);
                    });

                // Wait for all requests to complete
                await Promise.all([basicPromise, insightPromise, cardsPromise]);

            } catch (error) {
                console.error('Error setting up requests:', error);
            }
        };

        // Set the context for the today page
        // TODO: Use insight data to set the context
        const todayCustomLoadingTexts = [
            {
                text: "Checking your priorities for today",
                showOnce: true,
                minTime: 2500,
                maxTime: 3800
            },
            {
                text: "Analyzing your organizations",
                showOnce: true,
                minTime: 2000,
                maxTime: 3000
            },
            {
                text: "Thinking",
                showOnce: false,
                minTime: 1500,
                maxTime: 2500
            },
            {
                text: "Querying more data",
                showOnce: false,
                minTime: 2000,
                maxTime: 3000
            }
        ];

        // Set the context for the today page
        setCurrentAiPageContext(
            contextHelpers.createCustomContext(
                'today',
                'Today',
                {
                    date: new Date().toISOString(),
                    insightData: insightData ? insightData : null
                },
                todayCustomLoadingTexts
            )
        );

        fetchData();
    }, [axiosInstance]);

    return (
        <Layout>
            <SEO title="Today" />
            <div className="mx-auto space-y-6 pb-8">
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={fadeInVariants}
                    transition={{ duration: 0.5 }}
                >
                    <WelcomeRow 
                        loading={basicLoading} 
                        error={basicData === null && !basicLoading} 
                        data={basicData} 
                    />
                    
                    <BotInsight 
                        loading={insightLoading} 
                        error={insightData === null && !insightLoading} 
                        data={insightData} 
                    />
                    
                    <DashboardCards 
                        loading={cardsLoading} 
                        error={cardsData === null && !cardsLoading} 
                        data={cardsData}
                        navigate={navigate}
                    />
                </motion.div>
            </div>
        </Layout>
    );
};

export default TodayPage;
